import { useCallback, useEffect, useState } from "react";
import { KeyRound, RefreshCcw, Search, ShieldAlert, Tag, UserCog } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import PinConfirm from "../components/PinConfirm";
import { FibiLoading } from "../components/FibiLoading";

const ROLE_LABEL = { admin: "Super Admin", admin_fasnet: "Admin Fasnet", teknisi_kepala: "Teknisi Kepala", tuan_rumah: "Tuan Rumah", teknisi: "Teknisi", pending: "Menunggu Aktivasi" };
const ROLE_OPTIONS = ["admin", "admin_fasnet", "teknisi_kepala", "tuan_rumah", "teknisi"];
const ROLE_BADGE = {
  admin: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  admin_fasnet: "bg-sky-500/15 text-sky-500 border-sky-500/30",
  teknisi_kepala: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  tuan_rumah: "bg-violet-500/15 text-violet-500 border-violet-500/30",
  teknisi: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  pending: "bg-rose-500/15 text-rose-500 border-rose-500/30",
};
const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

export default function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [roleChange, setRoleChange] = useState(null); // { target, role }
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPin, setResetPin] = useState("");
  const [labelTarget, setLabelTarget] = useState(null);
  const [labelValue, setLabelValue] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch((err) => { toast.error(errMsg(err)); setUsers([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (user?.role !== "admin") {
    return (
      <Card className="max-w-md mx-auto mt-10" data-testid="users-forbidden">
        <CardContent className="p-6 text-center space-y-2">
          <ShieldAlert className="w-8 h-8 mx-auto text-rose-500" />
          <p className="font-heading font-semibold">Halaman ini khusus Super Admin</p>
          <p className="text-sm text-muted-foreground">Hanya Super Admin yang dapat mengelola pengguna, role, dan PIN.</p>
        </CardContent>
      </Card>
    );
  }

  const filtered = (users || []).filter((u) =>
    !q || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase()) || (u.agent_code || "").toLowerCase().includes(q.toLowerCase()));

  const doChangeRole = async () => {
    if (!roleChange) return;
    setBusy(true);
    try {
      await api.put(`/users/${roleChange.target.id}`, {
        name: roleChange.target.name || roleChange.target.email,
        role: roleChange.role,
        agent_code: roleChange.target.agent_code || "",
      });
      toast.success(`Role ${roleChange.target.email} diubah menjadi ${ROLE_LABEL[roleChange.role]}`);
      setRoleChange(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const doResetPin = async () => {
    if (!resetTarget) return;
    if (!/^\d{4,8}$/.test(resetPin)) { toast.error("PIN harus 4-8 digit angka"); return; }
    setBusy(true);
    try {
      await api.post(`/users/${resetTarget.id}/reset-pin`, { pin: resetPin });
      toast.success(`PIN ${resetTarget.name || resetTarget.email} direset — PIN baru: ${resetPin}. Sesi aktifnya dikeluarkan, minta login ulang.`);
      setResetTarget(null);
      setResetPin("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const doSaveLabel = async () => {
    if (!labelTarget) return;
    setBusy(true);
    try {
      await api.put(`/users/${labelTarget.id}`, {
        name: labelTarget.name || labelTarget.email,
        role: labelTarget.role,
        agent_code: labelTarget.agent_code || "",
        chat_label: labelValue,
      });
      toast.success(labelValue.trim() ? `Label chat ${labelTarget.email} diubah menjadi "${labelValue.trim()}"` : `Label chat ${labelTarget.email} dikosongkan — kembali ke nomor otomatis`);
      setLabelTarget(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="user-management-page">
      <div>
        <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2">
          <UserCog className="w-6 h-6 text-[#F04E23]" /> Manajemen User
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Kelola role & PIN semua pengguna. Perubahan role berlaku seketika; reset PIN mengeluarkan sesi aktif pengguna tersebut.</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 space-y-0">
          <CardTitle className="text-base font-heading">Daftar Pengguna ({filtered.length})</CardTitle>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9 h-9" placeholder="Cari nama / email / kode agen..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="input-search-user" />
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {users === null ? (
            <div><FibiLoading text="FIBI sedang memuat..." size={20} /></div>
          ) : (
            <table className="w-full text-sm min-w-[760px]" data-testid="users-table">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2.5 w-10">No.</th>
                  <th className="px-4 py-2.5">Nama</th>
                  <th className="px-4 py-2.5">Email</th>
                  <th className="px-4 py-2.5">Role</th>
                  <th className="px-4 py-2.5">Label Chat</th>
                  <th className="px-4 py-2.5">Kode Agen</th>
                  <th className="px-4 py-2.5 text-right">Saldo</th>
                  <th className="px-4 py-2.5 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((u, i) => {
                  const isSelf = u.id === user?.id || u.email === user?.email;
                  return (
                    <tr key={u.id} className="hover:bg-accent/40" data-testid={`user-row-${u.email}`}>
                      <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{i + 1}</td>
                      <td className="px-4 py-2.5 font-medium">{u.name || "-"} {isSelf && <span className="text-[10px] text-muted-foreground">(Anda)</span>}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{u.email}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${ROLE_BADGE[u.role] || ROLE_BADGE.pending}`} data-testid={`user-role-${u.email}`}>{ROLE_LABEL[u.role] || u.role}</span>
                          <Select value="" onValueChange={(v) => setRoleChange({ target: u, role: v })} disabled={isSelf}>
                            <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`select-role-${u.email}`}><SelectValue placeholder="Ubah role..." /></SelectTrigger>
                            <SelectContent>
                              {ROLE_OPTIONS.filter((r) => r !== u.role).map((r) => (
                                <SelectItem key={r} value={r} data-testid={`role-opt-${r}`}>{ROLE_LABEL[r]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        {u.chat_label ? (
                          <span className="px-2 py-0.5 rounded-full border text-[10px] font-bold bg-[#F04E23]/15 text-[#F04E23] border-[#F04E23]/30" data-testid={`user-label-${u.email}`}>{u.chat_label}</span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground" data-testid={`user-label-${u.email}`}>{["admin", "admin_fasnet"].includes(u.role) ? "otomatis" : "-"}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs">{u.agent_code || "-"}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">{rupiah(u.balance)}</td>
                      <td className="px-4 py-2.5 text-right space-x-1.5">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setLabelTarget(u); setLabelValue(u.chat_label || ""); }} data-testid={`btn-label-${u.email}`}>
                          <Tag className="w-3 h-3 mr-1" /> Label
                        </Button>
                        {u.role === "admin_fasnet" && (
                          <Button size="sm" variant={u.shift_work ? "secondary" : "outline"} className={`h-7 text-xs ${u.shift_work ? "text-amber-400" : ""}`} title={u.shift_work ? "Kerja shift: Pagi 08.00–15.30 / Sore 15.00–22.00 (klik untuk kembali reguler 08.30–17.00)" : "Jadikan kerja shift (Pagi 08.00–15.30 / Sore 15.00–22.00)"}
                            onClick={async () => { try { await api.put(`/users/${u.id}`, { name: u.name || u.email, role: u.role, agent_code: u.agent_code || "", chat_label: u.chat_label || "", shift_work: !u.shift_work }); toast.success(`${u.name || u.email}: ${!u.shift_work ? "kerja SHIFT (pagi/sore)" : "jam reguler 08.30–17.00"}`); load(); } catch (err) { toast.error(errMsg(err)); } }}
                            data-testid={`btn-shift-${u.email}`}>
                            {u.shift_work ? "Shift ✓" : "Shift"}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setResetTarget(u); setResetPin(""); }} data-testid={`btn-reset-pin-${u.email}`}>
                          <KeyRound className="w-3 h-3 mr-1" /> Reset PIN
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {!filtered.length && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="users-empty">Tidak ada pengguna yang cocok.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <PinConfirm
        open={!!roleChange}
        onOpenChange={() => setRoleChange(null)}
        title="Ubah Role Pengguna"
        description={roleChange ? `Ubah role ${roleChange.target.email} menjadi ${ROLE_LABEL[roleChange.role]}? Masukkan PIN Anda untuk konfirmasi.` : ""}
        confirmLabel="Ubah Role"
        testid="pin-change-role"
        onConfirm={doChangeRole}
      />

      <Dialog open={!!labelTarget} onOpenChange={() => setLabelTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="dialog-chat-label">
          <DialogHeader>
            <DialogTitle className="text-base font-heading">Label Chat — {labelTarget?.name || labelTarget?.email}</DialogTitle>
            <DialogDescription className="text-xs">Label ini tampil sebagai nama admin di chat, komplain, dan mutasi saldo (mis. Admin1, Admin2). Kosongkan untuk kembali ke nomor otomatis sesuai urutan pendaftaran admin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Label Chat</Label>
              <Input placeholder="mis. Admin5" value={labelValue} onChange={(e) => setLabelValue(e.target.value)} data-testid="input-chat-label" />
            </div>
            <Button className="w-full bg-[#F04E23] hover:bg-[#d9441d] text-white" disabled={busy} onClick={doSaveLabel} data-testid="btn-save-chat-label">
              <Tag className="w-4 h-4 mr-1.5" /> {busy ? "Memproses..." : "Simpan Label"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={() => setResetTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="dialog-reset-pin">
          <DialogHeader>
            <DialogTitle className="text-base font-heading">Reset PIN — {resetTarget?.name || resetTarget?.email}</DialogTitle>
            <DialogDescription className="text-xs">PIN baru 4-8 digit angka. Semua sesi aktif pengguna ini akan dikeluarkan dan ia harus login ulang.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>PIN Baru</Label>
              <Input type="text" inputMode="numeric" maxLength={8} placeholder="mis. 123456" value={resetPin} onChange={(e) => setResetPin(e.target.value.replace(/\D/g, ""))} data-testid="input-reset-pin" />
            </div>
            <Button className="w-full bg-[#F04E23] hover:bg-[#d9441d] text-white" disabled={busy} onClick={doResetPin} data-testid="btn-confirm-reset-pin">
              <RefreshCcw className="w-4 h-4 mr-1.5" /> {busy ? "Memproses..." : "Reset PIN"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
